import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import config from "../../../config";

type UnsubState = "idle" | "loading" | "done" | "already" | "error";

type UnsubMeta = {
  tenant_name: string;
  from_name: string | null;
  from_email: string | null;
  already_unsubscribed: boolean;
  logo_url: string | null;
};

/**
 * Fallback SPA de descadastro (links antigos / painel).
 * Fluxo canônico: HTML no host de tracking do cliente — sem redirect, sem marca Br Envio.
 */
const PublicUnsubscribe = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") || "").trim();
  const [state, setState] = useState<UnsubState>(token ? "idle" : "error");
  const [meta, setMeta] = useState<UnsubMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(Boolean(token));

  const apiUrl = useMemo(() => {
    const base = (config.api.API_URL || "").replace(/\/$/, "");
    return `${base}/t/unsubscribe/${encodeURIComponent(token)}`;
  }, [token]);

  const senderLabel = meta?.from_name?.trim() || meta?.tenant_name || "";

  document.title = t("public.unsubscribe.documentTitle");

  useEffect(() => {
    if (!token) {
      setMetaLoading(false);
      return;
    }

    let cancelled = false;
    const locale = localStorage.getItem("I18N_LANGUAGE") || "pt-BR";

    (async () => {
      try {
        const res = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Accept-Language": locale,
          },
        });
        const body = await res.json().catch(() => null);
        if (cancelled) {
          return;
        }
        if (!res.ok || !body?.success || !body?.data) {
          setState("error");
          return;
        }
        setMeta(body.data as UnsubMeta);
        if (body.data.already_unsubscribed) {
          setState("already");
        }
      } catch {
        if (!cancelled) {
          setState("error");
        }
      } finally {
        if (!cancelled) {
          setMetaLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiUrl, token]);

  const confirm = async () => {
    if (!token || state === "loading") {
      return;
    }
    setState("loading");
    try {
      const locale = localStorage.getItem("I18N_LANGUAGE") || "pt-BR";
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Accept-Language": locale,
        },
        body: "{}",
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.success) {
        setState("error");
        return;
      }
      setState(body.already_unsubscribed ? "already" : "done");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="auth-page-wrapper pt-5">
      <div className="auth-page-content mt-lg-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <div className="card mt-4 card-bg-fill">
                <div className="card-body p-4">
                  {/* Só logo do cliente; sem logo = espaço em branco (sem Br Envio). */}
                  {meta?.logo_url ? (
                    <div className="text-center mt-2 mb-4">
                      <img
                        src={meta.logo_url}
                        alt={meta.tenant_name || ""}
                        style={{ maxHeight: 48, maxWidth: 220 }}
                      />
                    </div>
                  ) : null}

                  {metaLoading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                    </div>
                  ) : !token || state === "error" ? (
                    <>
                      <h5 className="text-center mb-2">
                        {t("public.unsubscribe.invalidTitle")}
                      </h5>
                      <p className="text-muted text-center mb-0">
                        {t("public.unsubscribe.invalidBody")}
                      </p>
                    </>
                  ) : state === "done" || state === "already" ? (
                    <Alert color="success" className="mb-0 text-center">
                      <h5 className="alert-heading mb-2">
                        {t("public.unsubscribe.successTitle")}
                      </h5>
                      <p className="mb-2">
                        {state === "already"
                          ? t("public.unsubscribe.alreadyBody")
                          : t("public.unsubscribe.successBody", {
                              sender:
                                senderLabel ||
                                t("public.unsubscribe.senderFallback"),
                            })}
                      </p>
                      <p className="mb-0 small text-muted">
                        {t("public.unsubscribe.changedMind", {
                          sender:
                            senderLabel ||
                            t("public.unsubscribe.senderFallback"),
                        })}
                      </p>
                    </Alert>
                  ) : (
                    <>
                      {senderLabel ? (
                        <p className="text-center text-muted mb-2 small">
                          {t("public.unsubscribe.fromLabel", {
                            sender: senderLabel,
                          })}
                        </p>
                      ) : null}
                      <h5 className="text-center mb-2">
                        {t("public.unsubscribe.confirmTitle")}
                      </h5>
                      <p className="text-muted text-center mb-4">
                        {t("public.unsubscribe.confirmBody", {
                          sender:
                            senderLabel ||
                            t("public.unsubscribe.senderFallback"),
                        })}
                      </p>
                      <div className="d-grid">
                        <Button
                          color="danger"
                          disabled={state === "loading"}
                          onClick={() => void confirm()}
                        >
                          {state === "loading" ? (
                            <Spinner size="sm" className="me-2" />
                          ) : null}
                          {t("public.unsubscribe.confirmButton")}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default PublicUnsubscribe;
